/**
 * Agent Findings Store
 *
 * Generic Zustand store for the intelligence agents' findings, keyed by
 * agentType. Mirrors twinsStore's cluster lifecycle (active → accepted/dismissed).
 */

import { create } from 'zustand';
import {
  getActiveFindings,
  getFindingsHistory,
  acceptFinding,
  dismissFinding,
  type AgentFinding,
} from '../../lib/agentFindingsService';

// ── Ordering ──────────────────────────────────────────────────────────────────

const SEVERITY_RANK: Record<string, number> = {
  critical: 0, high: 1, medium: 2, low: 3, info: 4,
};

function bySeverityThenRecency(a: AgentFinding, b: AgentFinding): number {
  const sev = (SEVERITY_RANK[a.severity] ?? 5) - (SEVERITY_RANK[b.severity] ?? 5);
  if (sev !== 0) return sev;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

// ── State ─────────────────────────────────────────────────────────────────────

interface FindingsSlice {
  active: AgentFinding[];
  history: AgentFinding[];
  isLoading: boolean;
  error: string | null;
}

function emptySlice(): FindingsSlice {
  return { active: [], history: [], isLoading: false, error: null };
}

export interface AgentFindingsStoreState {
  byAgent: Record<string, FindingsSlice>;

  fetchActive: (workspaceId: string, agentType: string) => Promise<void>;
  fetchHistory: (workspaceId: string, agentType: string, limit?: number) => Promise<void>;
  accept: (agentType: string, findingId: string, userId: string) => Promise<{ success: boolean; error?: string }>;
  dismiss: (agentType: string, findingId: string, userId: string) => Promise<{ success: boolean; error?: string }>;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useAgentFindingsStore = create<AgentFindingsStoreState>((set, _get) => ({
  byAgent: {},

  fetchActive: async (workspaceId, agentType) => {
    set((s) => ({
      byAgent: { ...s.byAgent, [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), isLoading: true, error: null } },
    }));

    const { data, error } = await getActiveFindings(workspaceId, agentType);

    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: {
          ...(s.byAgent[agentType] ?? emptySlice()),
          active: error ? [] : [...data].sort(bySeverityThenRecency),
          isLoading: false,
          error,
        },
      },
    }));
  },

  fetchHistory: async (workspaceId, agentType, limit = 50) => {
    const { data, error } = await getFindingsHistory(workspaceId, agentType, limit);
    if (error) return;
    set((s) => ({
      byAgent: { ...s.byAgent, [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), history: data } },
    }));
  },

  accept: async (agentType, findingId, userId) => {
    const { data, error } = await acceptFinding(findingId, userId);
    if (error || !data) return { success: false, error: error ?? 'Accept failed' };
    set((s) => {
      const slice = s.byAgent[agentType] ?? emptySlice();
      return {
        byAgent: {
          ...s.byAgent,
          [agentType]: {
            ...slice,
            active: slice.active.filter((f) => f.id !== findingId),
            history: [data, ...slice.history],
          },
        },
      };
    });
    return { success: true };
  },

  dismiss: async (agentType, findingId, userId) => {
    const { data, error } = await dismissFinding(findingId, userId);
    if (error || !data) return { success: false, error: error ?? 'Dismiss failed' };
    set((s) => {
      const slice = s.byAgent[agentType] ?? emptySlice();
      return {
        byAgent: {
          ...s.byAgent,
          [agentType]: {
            ...slice,
            active: slice.active.filter((f) => f.id !== findingId),
            history: [data, ...slice.history],
          },
        },
      };
    });
    return { success: true };
  },
}));
